import React from 'react';
import View from "react-native-web/dist/exports/View";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Platform from "react-native-web/dist/exports/Platform";
import PropTypes from 'prop-types';
import Box from '../Box';
import { Button } from '../Button';
import { TextButton } from '../Link';
import Spacer from '../Spacer';
import Typography from '../Typography';
import { useThemeTokens } from '../ThemeProvider';
import { useViewport } from '../ViewportProvider';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ModalContent = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    heading,
    headingLevel = 'h3',
    subHeading,
    subHeadingSize = 'medium',
    bodyText,
    confirmButtonText,
    confirmButtonVariant = {
      priority: 'high'
    },
    onConfirm,
    tokens,
    variant,
    cancelButtonText,
    cancelButtonType: CancelButton = TextButton,
    children,
    onCancel,
    footer
  } = _ref;
  const viewport = useViewport();
  const {
    headingColor,
    cancelButtonColor,
    ...themeTokens
  } = useThemeTokens('Modal', tokens, variant, {
    viewport
  });
  const [scrollContainerHeight, setScrollContainerHeight] = React.useState(0);
  const [scrollContentHeight, setScrollContentHeight] = React.useState(0);
  const handleContainerLayout = _ref2 => {
    let {
      nativeEvent: {
        layout: {
          height
        }
      }
    } = _ref2;
    return setScrollContainerHeight(height);
  };
  const onContentSizeChange = (_, height) => setScrollContentHeight(height);
  const isContentOverflowing = scrollContentHeight > scrollContainerHeight;
  const hasConfirmButton = confirmButtonText !== undefined && onConfirm !== undefined;
  const hasCancelButton = cancelButtonText !== undefined && onCancel !== undefined;
  const hasHeadingSection = Boolean(heading || subHeading);
  const selectHeadingContainerStyles = _ref3 => {
    let {
      paddingLeft,
      paddingRight,
      marginLeft,
      marginRight,
      borderColor,
      headingBorderBottomWidth,
      headingPaddingRight,
      headingPaddingTop
    } = _ref3;
    return {
      flexDirection: 'column',
      marginLeft: -marginLeft,
      marginRight: -marginRight,
      paddingLeft,
      paddingTop: headingPaddingTop,
      paddingRight: paddingRight + headingPaddingRight,
      borderBottomColor: borderColor,
      borderBottomWidth: headingBorderBottomWidth
    };
  };
  const selectFooterContainerStyles = _ref4 => {
    let {
      paddingLeft,
      paddingRight,
      paddingTop,
      marginLeft,
      marginRight,
      borderColor,
      gap,
      direction,
      footerTopWidth,
      hasBorder
    } = _ref4;
    return {
      flexDirection: direction,
      alignItems: 'center',
      gap,
      marginLeft: -marginLeft,
      marginRight: -marginRight,
      paddingLeft,
      paddingRight,
      paddingTop,
      borderTopWidth: footerTopWidth > 0 ? footerTopWidth : hasBorder && 1 || 0,
      borderTopColor: borderColor
    };
  };
  const subheadingStyles = {
    marginTop: themeTokens.subHeadingMarginTop
  };
  return /*#__PURE__*/_jsxs(View, {
    style: styles.modalContent,
    ref: ref,
    children: [hasHeadingSection && /*#__PURE__*/_jsxs(View, {
      style: selectHeadingContainerStyles(themeTokens),
      children: [heading && /*#__PURE__*/_jsx(Typography, {
        variant: {
          size: headingLevel
        },
        tokens: {
          color: headingColor
        },
        heading: headingLevel,
        children: heading
      }), subHeading && /*#__PURE__*/_jsx(View, {
        style: subheadingStyles,
        children: /*#__PURE__*/_jsx(Typography, {
          variant: {
            size: subHeadingSize
          },
          children: subHeading
        })
      }), Boolean((bodyText || children) && hasHeadingSection) && /*#__PURE__*/_jsx(Spacer, {
        space: 3
      })]
    }), bodyText && /*#__PURE__*/_jsx(Box, {
      scroll: {
        onContentSizeChange,
        showsVerticalScrollIndicator: true
      },
      onLayout: handleContainerLayout,
      top: themeTokens.contentPaddingTop,
      bottom: themeTokens.contentPaddingBottom,
      children: /*#__PURE__*/_jsx(Typography, {
        children: bodyText
      })
    }), children, (hasConfirmButton || hasCancelButton) && !footer && /*#__PURE__*/_jsxs(View, {
      style: [selectFooterContainerStyles({
        ...themeTokens,
        hasBorder: isContentOverflowing
      })],
      children: [hasConfirmButton && /*#__PURE__*/_jsx(Button, {
        variant: confirmButtonVariant,
        tokens: {
          width: viewport === 'xs' || viewport === 'sm' ? '100%' : undefined
        },
        onPress: onConfirm,
        children: confirmButtonText
      }), hasCancelButton ? /*#__PURE__*/_jsx(View, {
        style: styles.cancelButton,
        children: /*#__PURE__*/_jsx(CancelButton, {
          tokens: {
            color: cancelButtonColor
          },
          onPress: onCancel,
          children: cancelButtonText
        })
      }) : null]
    }), footer]
  });
});
ModalContent.displayName = 'ModalContent';
const styles = StyleSheet.create({
  modalContent: {
    flex: 1,
    flexDirection: 'column',
    minHeight: Platform.OS === 'web' ? '100%' : 'auto'
  },
  cancelButton: {
    display: 'flex'
  }
});
ModalContent.propTypes = {
  tokens: PropTypes.object,
  variant: PropTypes.object,
  heading: PropTypes.string,
  headingLevel: PropTypes.oneOf(['h3', 'h4']),
  subHeading: PropTypes.string,
  subHeadingSize: PropTypes.oneOf(['small', 'medium', 'large']),
  bodyText: PropTypes.string,
  confirmButtonText: PropTypes.string,
  confirmButtonVariant: PropTypes.object,
  onConfirm: PropTypes.func,
  cancelButtonText: PropTypes.string,
  cancelButtonType: PropTypes.elementType,
  // TODO: figure out a way of passing an icon to the TextButton
  children: PropTypes.node,
  onCancel: PropTypes.func,
  footer: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)])
};
export default ModalContent;